import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { normalizeText } from "../utils/textUtils";

export default function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = "Všetky",
  searchPlaceholder = "Hľadať...",
  minWidth = 160,
  formatOption = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  const display = formatOption || (o => o);

  const updateDropdownPosition = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const dropW = Math.max(minWidth, 220);
    let left = rect.left + window.scrollX;
    // prevent going off right edge
    if (left + dropW > window.innerWidth - 8) {
      left = window.innerWidth - dropW - 8;
    }
    setDropdownStyle({
      position: "absolute",
      top: rect.bottom + window.scrollY + 4,
      left,
      zIndex: 99999,
      minWidth: dropW,
      background: "#fff",
      border: "1px solid #ccc",
      borderRadius: 6,
      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      display: "flex",
      flexDirection: "column",
    });
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = () => updateDropdownPosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  const handleToggleOpen = () => {
    if (!open) {
      updateDropdownPosition();
    }
    setOpen(o => !o);
    setSearch("");
  };

  const filtered = options.filter(o =>
    !search || normalizeText(display(o)).includes(normalizeText(search))
  );

  const toggleOption = (opt) => {
    onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (display(selected[0]).length > 22 ? display(selected[0]).slice(0, 22) + "…" : display(selected[0]))
      : `Vybrané: ${selected.length}`;

  const dropdown = open ? ReactDOM.createPortal(
    <div ref={dropdownRef} style={dropdownStyle}>
      <div style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          style={{
            width: "100%", boxSizing: "border-box",
            fontSize: 13, border: "1px solid #ddd", borderRadius: 4,
            padding: "4px 8px", color: "#222",
          }}
          onClick={e => e.stopPropagation()}
        />
      </div>
      {selected.length > 0 && (
        <button
          onClick={() => onChange([])}
          style={{
            display: "block", width: "100%", textAlign: "left",
            padding: "5px 12px", fontSize: 12, color: "#888",
            background: "none", border: "none", borderBottom: "1px solid #eee",
            cursor: "pointer",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "#c00"; e.currentTarget.style.background = "#fff5f5"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#888"; e.currentTarget.style.background = "none"; }}
        >✕ Zrušiť výber</button>
      )}
      <div style={{ overflowY: "auto", maxHeight: 220 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "8px 12px", color: "#aaa", fontSize: 13 }}>Žiadne výsledky</div>
        ) : filtered.map(opt => (
          <label
            key={opt}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 12px", fontSize: 13, cursor: "pointer", color: "#222",
              userSelect: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f0f6ff"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => toggleOption(opt)}
              style={{ cursor: "pointer", width: 14, height: 14, flexShrink: 0 }}
            />
            {display(opt)}
          </label>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={btnRef}
        onClick={handleToggleOpen}
        style={{
          padding: "5px 10px", fontSize: 13, borderRadius: 4,
          border: "1px solid #ccc", cursor: "pointer",
          background: selected.length > 0 ? "#e8f4fd" : "#fff",
          display: "flex", alignItems: "center", gap: 6,
          whiteSpace: "nowrap", minWidth, justifyContent: "space-between",
          color: "#222",
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: 10, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>
      {dropdown}
    </div>
  );
}
